import React, { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

function fmtLastLogin(ts) {
  if (!ts) return "Never";
  const d = new Date(ts);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${time}`;
}

const ROLE_COLORS = {
  super_admin: { bg: "#7C3AED18", border: "#7C3AED44", text: "#a78bfa" },
  admin:       { bg: "#2563EB18", border: "#2563EB44", text: "#60a5fa" },
  editor:      { bg: "#059669018", border: "#05966944", text: "#34d399" },
  viewer:      { bg: "#64748b18", border: "#64748b44", text: "#94a3b8" },
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin:       "Admin",
  editor:      "Editor",
  viewer:      "Viewer",
};

function StatusBadge({ active }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
      background: active ? "#22c55e18" : "#DC262618",
      color:      active ? "#22c55e"   : "#DC2626",
      border:     `1px solid ${active ? "#22c55e33" : "#DC262633"}`,
    }}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export function UsersList({ currentUser, isSuperAdmin, theme: t, isMobile, refreshKey, onAdd, onEdit }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [msg,     setMsg]     = useState("");
  const [search,  setSearch]  = useState("");
  const [roleFilter,   setRoleFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users`, );
      if (!res.ok) throw new Error((await res.json()).error || "Failed to load users");
      setUsers(await res.json());
      setError("");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  async function handleDeactivate(u) {
    if (!window.confirm(`Deactivate ${u.name}?`)) return;
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users/${u.id}/deactivate`, { method: "PUT" });
      if (!res.ok) throw new Error((await res.json()).error);
      setMsg(`✓ ${u.name} deactivated.`);
      load();
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  async function handleActivate(u) {
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users/${u.id}/activate`, { method: "PUT" });
      if (!res.ok) throw new Error((await res.json()).error);
      setMsg(`✓ ${u.name} reactivated.`);
      load();
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  async function handleResetPw(u) {
    if (!window.confirm(`Reset password for ${u.name}? Temp credentials will be logged to the server console.`)) return;
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users/${u.id}/reset-password`, { method: "POST" });
      if (!res.ok) throw new Error((await res.json()).error);
      setMsg(`✓ Password reset for ${u.name}. Temp password logged to server console.`);
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  async function handleResendVerification(u) {
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users/${u.id}/resend-verification`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (data.emailSent) {
        setMsg(`✓ Verification email resent to ${u.email}.`);
      } else {
        setMsg(`✗ Verification link refreshed for ${u.email}, but the email failed to send: ${data.emailError || "unknown error"}`);
      }
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  async function handleDelete(u) {
    if (!window.confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users/${u.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error);
      setMsg(`✓ ${u.name} deleted.`);
      load();
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  // Can the current user act on the target user?
  function canAct(target) {
    if (target.id === currentUser.id) return false; // own account
    if (target.role === "super_admin" && !isSuperAdmin) return false;
    return true;
  }

  const filtered = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole   = roleFilter === "all"   || u.role === roleFilter;
    const matchStatus = statusFilter === "all" || (statusFilter === "active" ? u.active : !u.active);
    return matchSearch && matchRole && matchStatus;
  });

  const permSummary = (u) => {
    if (!u.permissions) return u.access === "all" ? "Full Access" : u.access || "—";
    const count = Object.values(u.permissions).filter(Boolean).length;
    const total = Object.keys(u.permissions).length;
    return count === total ? "Full Access" : count === 0 ? "No Access" : `${count}/${total} sections`;
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>User Management</h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>{users.length} users · @alphamedia.co.zw only</p>
        </div>
        <button onClick={onAdd}
          style={{ background: "#2563EB", border: "none", borderRadius: 10, padding: "9px 18px", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "Georgia, serif" }}>
          + Add User
        </button>
      </div>

      {msg && (
        <div style={{ background: msg.startsWith("✓") ? "#22c55e11" : "#DC262611", border: `1px solid ${msg.startsWith("✓") ? "#22c55e33" : "#DC262633"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: msg.startsWith("✓") ? "#22c55e" : "#DC2626", display: "flex", justifyContent: "space-between" }}>
          <span>{msg}</span>
          <button onClick={() => setMsg("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 14 }}>×</button>
        </div>
      )}

      {/* Filters row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          placeholder="Search name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: "1 1 200px", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", color: t.text, fontSize: 12, outline: "none" }}
        />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", color: t.text, fontSize: 12, cursor: "pointer" }}>
          <option value="all">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", color: t.text, fontSize: 12, cursor: "pointer" }}>
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: t.textFaint, fontSize: 13 }}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#DC2626", fontSize: 13 }}>⚠️ {error}</div>
      ) : (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: isMobile ? 600 : "auto" }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                {["Name", "Email", "Role", "Status", "Verified", "PW Expires", "Last Login", "Permissions", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", color: t.textMuted, fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: "32px 16px", textAlign: "center", color: t.textFaint, fontSize: 13 }}>No users match your filters.</td></tr>
              )}
              {filtered.map(u => {
                const rc = ROLE_COLORS[u.role] || ROLE_COLORS.viewer;
                const actable = canAct(u);
                return (
                  <tr key={u.id}
                    style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "11px 16px", color: t.text, fontWeight: 700, whiteSpace: "nowrap" }}>
                      {u.name}
                      {u.forcePasswordChange && <span style={{ marginLeft: 6, fontSize: 9, color: "#f59e0b", background: "#f59e0b18", border: "1px solid #f59e0b33", padding: "1px 5px", borderRadius: 10 }}>Temp PW</span>}
                    </td>
                    <td style={{ padding: "11px 16px", color: t.textSub, fontSize: 12 }}>{u.email}</td>
                    <td style={{ padding: "11px 16px" }}>
                      <span style={{ fontSize: 10, background: rc.bg, border: `1px solid ${rc.border}`, borderRadius: 6, padding: "2px 8px", color: rc.text, fontWeight: 700, whiteSpace: "nowrap" }}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td style={{ padding: "11px 16px" }}><StatusBadge active={u.active} /></td>
                    <td style={{ padding: "11px 16px" }}>
                      {u.emailVerified === false ? (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: "#f59e0b18", color: "#f59e0b", border: "1px solid #f59e0b33" }}>Pending</span>
                      ) : (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: "#22c55e18", color: "#22c55e", border: "1px solid #22c55e33" }}>Verified</span>
                      )}
                    </td>
                    <td style={{ padding: "11px 16px" }}>
                      {u.passwordExpiresAt ? (() => {
                        const days = Math.ceil((new Date(u.passwordExpiresAt) - Date.now()) / 86400000);
                        const color = days < 0 ? "#DC2626" : days <= 7 ? "#f59e0b" : "#22c55e";
                        const bg    = days < 0 ? "#DC262618" : days <= 7 ? "#f59e0b18" : "#22c55e18";
                        const brd   = days < 0 ? "#DC262633" : days <= 7 ? "#f59e0b33" : "#22c55e33";
                        return (
                          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: bg, color, border: `1px solid ${brd}` }}>
                            {days < 0 ? "Expired" : `${days}d`}
                          </span>
                        );
                      })() : <span style={{ color: t.textFaint, fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ padding: "11px 16px", color: t.textFaint, fontSize: 11, whiteSpace: "nowrap" }}>
                      {fmtLastLogin(u.lastLogin)}
                    </td>
                    <td style={{ padding: "11px 16px", color: t.textSub, fontSize: 11 }}>{permSummary(u)}</td>
                    <td style={{ padding: "11px 16px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        {actable && (
                          <button onClick={() => onEdit(u.id)} title="Edit user"
                            style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13 }}>
                            ✏️
                          </button>
                        )}
                        <button onClick={() => handleResetPw(u)} title="Reset password"
                          style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13 }}>
                          🔒
                        </button>
                        {u.emailVerified === false && (
                          <button onClick={() => handleResendVerification(u)} title="Resend verification email"
                            style={{ background: "none", border: "1px solid #f59e0b33", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13 }}>
                            📧
                          </button>
                        )}
                        {actable && u.active && (
                          <button onClick={() => handleDeactivate(u)} title="Deactivate user"
                            style={{ background: "none", border: "1px solid #f59e0b33", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13 }}>
                            ⏸️
                          </button>
                        )}
                        {actable && !u.active && (
                          <button onClick={() => handleActivate(u)} title="Reactivate user"
                            style={{ background: "none", border: "1px solid #22c55e33", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13 }}>
                            ▶️
                          </button>
                        )}
                        {actable && u.id !== "usr_admin" && (
                          <button onClick={() => handleDelete(u)} title="Delete user"
                            style={{ background: "none", border: "1px solid #DC262633", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 13 }}>
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
